import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'

import * as gql from './gql'
import type { TypeParams } from './types'

/** Space clusters API service. */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(organizations.Feature)

  /** @inheritdoc */
  public list = ec.models.Api.list.using.nothingButContext<TypeParams>(
    context => {
      if (!context) {
        return rxjs.of([])
      }

      return gql.queries.clusters.executor(context)
    },
  )

  /** @inheritdoc */
  public get: ec.models.Api<TypeParams>['get'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.queries.cluster.executor({
      ...context,
      clusterId: id,
    })
  }

  /** @inheritdoc */
  public create: ec.models.Api<TypeParams>['create'] = (context, input) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.createCluster.executor({
      ...context,
      input: gql.inputs.cluster.INPUT.getGqlFrom(input),
    })
  }

  /** @inheritdoc */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.updateCluster.executor({
      ...context,
      clusterId: id,
      input: gql.inputs.cluster.UPDATES.getGqlFrom(updates),
    })
  }

  /** @inheritdoc */
  public delete: ec.models.Api<TypeParams>['delete'] = (context, id) => {
    TypeAsserter.assertIsNotNullish(context)

    return gql.mutations.deleteCluster.executor({
      ...context,
      id,
    })
  }

  /** @inheritdoc */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    this._schemaFeature.selectors.schema.asObservable.pipe(
      // NOTE: The schema starts as an empty placeholder, so wait for the loaded
      // one to avoid emitting columns without organization entities
      rxjs.filter(schema => schema.areModelsInitialized),
      rxjs.switchMap(schema => {
        const orgEntities = schema.getEntitiesInDimension(
          organizations.constants.DIMENSION_ID,
        )

        const baseAttributes: ec.models.Attribute<TypeParams>[] = [
          ec.models.Attribute.fromKey<TypeParams>('name', {
            name: 'Name',
          }),
          ec.models.Attribute.fromKey<TypeParams>('description', {
            name: 'Description',
          }),
          {
            getValueFrom: cluster => cluster.assignedSpaceIds.length,
            id: 'assignedSpaceIds',
            name: 'Associated Records Count',
            valueInfo: {
              type: ents.core.common.value.types.Intrinsic.Integer,
            },
          },
        ]

        if (orgEntities.length === 0) {
          return rxjs.of(baseAttributes)
        }

        return rxjs
          .combineLatest(
            orgEntities.map(entity =>
              this._organizationsFeature.selectors
                .getOrganizationEc(entity.id)
                .asObservable.pipe(
                  rxjs.switchMap(
                    orgEc =>
                      orgEc?.plugins.cachePlugin.feature.selectors.lookup
                        .asObservable ?? rxjs.of(undefined),
                  ),
                  rxjs.map(
                    lookup =>
                      ({
                        getValueFrom: cluster => {
                          const orgId = cluster.organization?.[entity.id]
                          return orgId ? lookup?.[orgId]?.name : undefined
                        },
                        id: entity.id,
                        name: entity.name,
                      }) satisfies ec.models.Attribute<TypeParams>,
                  ),
                ),
            ),
          )
          .pipe(
            rxjs.map(orgAttributes => [...baseAttributes, ...orgAttributes]),
          )
      }),
    )
}
